import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixSettingsV1SiteCurrency from './settings-v1-site-currency-site-settings.http.js';

/**
 * A currency that can be displayed on a site.
 * Site currencies control which currencies visitors can view prices in.
 * When a currency is enabled for a site, prices are automatically converted from their original currency using up-to-date exchange rates.
 */
export interface SiteCurrency {
  /**
   * Three-letter currency code, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.
   * @maxLength 3
   * @format CURRENCY
   */
  code?: string;
}

export interface SetCurrenciesRequest {
  /**
   * List of currencies to enable for the site.
   * This list replaces any previously configured currencies.
   * Pass an empty array to disable currency conversion.
   * @maxSize 156
   */
  currencies?: SiteCurrency[];
}

export interface SetCurrenciesResponse {}

export interface GetCurrenciesRequest {}

export interface GetCurrenciesResponse {
  /** List of currencies currently enabled for the site. */
  currencies?: SiteCurrency[];
}

export interface SetCheckoutCurrenciesRequest {
  /**
   * The new checkout currency list
   * @maxSize 156
   */
  currencies?: SiteCurrency[];
}

export interface SetCheckoutCurrenciesResponse {}

export interface GetCheckoutCurrenciesRequest {}

export interface GetCheckoutCurrenciesResponse {
  /** Currencies supported in checkout */
  currencies?: SiteCurrency[];
}

/**
 * Sets the list of currencies that can be displayed on the site.
 *
 * When you set currencies for a site, visitors can view prices in any of these currencies.
 * Prices are automatically converted using up-to-date exchange rates.
 * The supplied list completely replaces the previous currency list.
 *
 * To disable currency conversion entirely, pass an empty array.
 * @public
 * @permissionId CURRENCY_CONVERTER.MODIFY_SITE_CURRENCIES
 * @applicableIdentity APP
 * @fqn com.wixpress.currency.site.settings.api.v1.SiteCurrencySettings.SetCurrencies
 */
export async function setCurrencies(
  options?: SetCurrenciesOptions
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    currencies: options?.currencies,
  });

  const reqOpts = ambassadorWixSettingsV1SiteCurrency.setCurrencies(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { currencies: '$[0].currencies' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface SetCurrenciesOptions {
  /**
   * List of currencies to enable for the site.
   * This list replaces any previously configured currencies.
   * Pass an empty array to disable currency conversion.
   * @maxSize 156
   */
  currencies?: SiteCurrency[];
}

/**
 * Retrieves the list of currencies that can be displayed on the site.
 *
 * Returns all currencies that are currently enabled for the site.
 * Visitors can view prices in any of these currencies.
 * @public
 * @permissionId CURRENCY_CONVERTER.READ_SITE_CURRENCIES
 * @applicableIdentity APP
 * @fqn com.wixpress.currency.site.settings.api.v1.SiteCurrencySettings.GetCurrencies
 */
export async function getCurrencies(): Promise<
  NonNullablePaths<
    GetCurrenciesResponse,
    `currencies` | `currencies.${number}.code`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts = ambassadorWixSettingsV1SiteCurrency.getCurrencies(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
